// import * as mutations from './mutation-types'
// import { MAKE, OFFER, BUY, EXCHANGE } from './action-types'
import {dbLogs, db} from '../firebase'
import { buildAuthorizationForCompany, setStoredActiveCompanyUid } from '../companyAccess'
import { getListener, setListener, detachListener } from './firebaseListeners'
import { readInventoryLots } from '../inventory/inventoryService'

let pendingInventoryLotsLoad = null

export default {
  newLog ({state}, payload) {
    dbLogs.ref('logs').child('transporter').child(state.authorization.company).push().set(payload)
  },

  switchActiveCompany ({ state, commit }, companyUID) {
    if (!state.primaryAuthorization || !companyUID) return Promise.resolve(false)

    const company = state.availableCompanies.find(company => company.uid === companyUID)
    if (!company) return Promise.resolve(false)

    setStoredActiveCompanyUid(company.uid)
    commit('setAuthorization', buildAuthorizationForCompany(state.primaryAuthorization, company))
    commit('clearDeliveries')
    commit('clearZones')
    commit('clearInventoryLots')
    commit('CLEAR_SEARCH')
    return Promise.resolve(true)
  },

  updateActiveCompanySeen ({ state, commit }, isUpdateSeen) {
    const authorization = state.authorization
    const user = state.user
    if (!authorization || !user || !user.uid) return Promise.resolve()

    const primaryCompanyUID = state.primaryAuthorization && state.primaryAuthorization.company
    const isSisterCompany = authorization.isSisterCompany || (primaryCompanyUID && authorization.company !== primaryCompanyUID)
    const updateRef = isSisterCompany
      ? db.ref('users/transportersman').child(user.uid).child('sisterCompanies').child(authorization.company)
      : db.ref('users/transportersman').child(user.uid)

    return updateRef.update({ isUpdateSeen }).then(() => {
      commit('setCompanyUpdateSeen', {
        companyUID: authorization.company,
        isUpdateSeen
      })
    })
  },

  subscribeToDeliveries ({ state, commit }) {
    if (state.deliveriesLoaded && getListener('deliveries')) {
      return Promise.resolve()
    }

    const companyUID = state.authorization && state.authorization.company
    if (!companyUID) {
      console.warn('Cannot subscribe to deliveries: no company UID')
      return Promise.resolve()
    }

    const waitForDeliveriesLoaded = () => {
      return new Promise((resolve) => {
        const timeoutMs = 10000
        const startTime = Date.now()

        const checkLoaded = () => {
          if (state.deliveriesLoaded || (Date.now() - startTime) > timeoutMs) {
            resolve()
            return
          }
          setTimeout(checkLoaded, 50)
        }

        checkLoaded()
      })
    }

    if (getListener('deliveries')) {
      return waitForDeliveriesLoaded()
    }

    const ref = db.ref('database/transporter/' + companyUID + '/deliveries/')
    const pendingOperations = new Map()
    let flushTimer = null
    const canMutateStore = () => getListener('deliveries') === ref

    const flushPendingOperations = () => {
      if (flushTimer) {
        clearTimeout(flushTimer)
        flushTimer = null
      }

      if (!canMutateStore()) {
        pendingOperations.clear()
        return
      }

      if (!pendingOperations.size) return

      const upserts = []
      const removals = []

      pendingOperations.forEach((operation, key) => {
        if (operation.type === 'remove') {
          removals.push(key)
        } else if (operation.delivery) {
          upserts.push(operation.delivery)
        }
      })

      pendingOperations.clear()

      if (removals.length) {
        commit('removeDeliveriesBatch', removals)
      }
      if (upserts.length) {
        commit('upsertDeliveriesBatch', upserts)
      }
    }

    const queueOperation = (key, operation) => {
      if (!canMutateStore()) return
      pendingOperations.set(key, operation)
      if (flushTimer) return
      flushTimer = setTimeout(flushPendingOperations, 32)
    }

    const childChangedHandler = (snapshot) => {
      queueOperation(snapshot.key, {
        type: 'upsert',
        delivery: {
          '.key': snapshot.key,
          ...snapshot.val()
        }
      })
    }

    const childRemovedHandler = (snapshot) => {
      queueOperation(snapshot.key, { type: 'remove' })
    }

    ref.on('child_changed', childChangedHandler)
    ref.on('child_removed', childRemovedHandler)

    const firstLoadPromise = ref.once('value')
      .then((snapshot) => {
        if (!canMutateStore()) return

        const deliveries = []
        snapshot.forEach((child) => {
          deliveries.push({
            '.key': child.key,
            ...child.val()
          })
        })
        commit('setDeliveries', deliveries)
        commit('setDeliveriesLoaded', true)

        // Register additions after initial snapshot and ignore already-loaded keys.
        const childAddedHandler = (childSnapshot) => {
          if (typeof state.deliveriesIndexByKey[childSnapshot.key] === 'number') return
          queueOperation(childSnapshot.key, {
            type: 'upsert',
            delivery: {
              '.key': childSnapshot.key,
              ...childSnapshot.val()
            }
          })
        }
        ref.on('child_added', childAddedHandler)

        flushPendingOperations()
      })
      .catch((error) => {
        console.error('[STORE] Error loading deliveries:', error)
        commit('setDeliveriesLoaded', true)
      })

    setListener('deliveries', ref)
    return firstLoadPromise
  },

  unsubscribeFromDeliveries () {
    detachListener('deliveries')
  },

  subscribeToZones ({ state, commit }) {
    if (state.zonesLoaded) {
      return Promise.resolve()
    }

    const companyUID = state.authorization && state.authorization.company
    if (!companyUID) {
      console.warn('Cannot subscribe to zones: no company UID')
      return Promise.resolve()
    }

    const waitForZonesLoaded = () => {
      return new Promise((resolve) => {
        const timeoutMs = 10000
        const startTime = Date.now()

        const checkLoaded = () => {
          if (state.zonesLoaded || (Date.now() - startTime) > timeoutMs) {
            resolve()
            return
          }
          setTimeout(checkLoaded, 50)
        }

        checkLoaded()
      })
    }

    if (getListener('zones')) {
      return waitForZonesLoaded()
    }

    const ref = db.ref('database/zones/transporter/' + companyUID + '/zone')
    let initialLoadResolved = false

    const firstLoadPromise = new Promise((resolve) => {
      ref.on('value', (snapshot) => {
        const zones = []
        if (snapshot.exists()) {
          snapshot.forEach((child) => {
            zones.push({
              id: child.key,
              ...child.val()
            })
          })
        }
        commit('setZones', zones)
        commit('setZonesLoaded', true)

        if (!initialLoadResolved) {
          initialLoadResolved = true
          resolve()
        }
      }, (error) => {
        console.error('[STORE] Error subscribing to zones:', error)
        if (!initialLoadResolved) {
          initialLoadResolved = true
          resolve()
        }
      })
    })

    setListener('zones', ref)
    return firstLoadPromise
  },

  unsubscribeFromZones () {
    detachListener('zones')
  },

  loadInventoryLots ({ state, commit }, options) {
    const force = Boolean(options && options.force)
    const companyUID = state.authorization && state.authorization.company
    if (!companyUID) return Promise.resolve([])
    if (state.inventoryLotsLoaded && !force) return Promise.resolve(state.inventoryLots)
    if (pendingInventoryLotsLoad && pendingInventoryLotsLoad.companyUID === companyUID && !force) {
      return pendingInventoryLotsLoad.promise
    }

    const settle = () => {
      if (pendingInventoryLotsLoad && pendingInventoryLotsLoad.promise === promise) pendingInventoryLotsLoad = null
    }
    const promise = readInventoryLots({ companyUID })
      .then(lots => {
        settle()
        const stillActive = state.authorization && state.authorization.company === companyUID
        if (stillActive) commit('setInventoryLots', lots)
        return lots
      })
      .catch(error => {
        settle()
        console.error('[STORE] Error loading inventory lots:', error)
        return state.inventoryLots
      })

    pendingInventoryLotsLoad = { companyUID, promise }
    return promise
  },

  reloadZones ({ dispatch, commit }) {
    dispatch('unsubscribeFromZones')
    commit('setZonesLoaded', false)
    commit('setZones', [])
    return dispatch('subscribeToZones')
  }
}
